import React, { useMemo, useState } from "react"
import Chart from "react-apexcharts"
import { getWeekTooltipLabel } from "utils/dateUtil"

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  {
    key: "chronological",
    icon: "⇅",
    label: "Sort",
    color: "#64748b",
    bg: "#f1f5f9",
    border: "#e2e8f0",
  },
  {
    key: "ascending",
    icon: "↑",
    label: "Low → High",
    color: "#2563eb",
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
  {
    key: "descending",
    icon: "↓",
    label: "High → Low",
    color: "#0f766e",
    bg: "#f0fdfa",
    border: "#99f6e4",
  },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: current.bg,
        border: `1.5px solid ${current.border}`,
        borderRadius: "10px",
        padding: "5px 12px",
        cursor: "pointer",
        outline: "none",
        transition: "all 0.18s ease",
        whiteSpace: "nowrap",
        userSelect: "none",
        minWidth: "115px",
      }}
    >
      <span
        style={{
          fontSize: "15px",
          lineHeight: 1,
          color: current.color,
          fontWeight: 700,
          minWidth: "16px",
          textAlign: "center",
        }}
      >
        {current.icon}
      </span>
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <span
          style={{
            fontSize: "8px",
            fontWeight: 700,
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            lineHeight: 1.1,
          }}
        >
          Sort Order
        </span>
        <span
          style={{
            fontSize: "11px",
            fontWeight: 700,
            color: current.color,
            lineHeight: 1.3,
          }}
        >
          {current.label}
        </span>
      </span>
    </button>
  )
}

const AreaLineChart = ({
  title,
  data = [],
  data2 = [],
  allWeeks = [],
  dateByWeek = {},
  prevDateByWeek = {},
  pound,
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const handleToggle = () => {
    const next =
      sortOrder === "chronological"
        ? "ascending"
        : sortOrder === "ascending"
        ? "descending"
        : "chronological"
    setSortOrder(next)
  }

  const gradientColors = pound ? ["#6C63FF", "#48CAE4"] : ["#F97316", "#FBBF24"]
  const hasSecond = data2?.length > 0

  // Derive year numbers from the date maps (format "DD/MM/YYYY" or "DD-MM-YYYY")
  const currentYear =
    Object.values(dateByWeek)[0]?.split(/[-/]/)?.[2] || "Current Year"
  const previousYearRaw = Object.values(prevDateByWeek)[0]?.split(/[-/]/)?.[2]
  const previousYear = previousYearRaw || "Previous Year"
  const hasPreviousYearData = !!previousYearRaw

  // ── Apply Sort Order to indices ───────────────────────────────────────────
  const sortedIndices = useMemo(() => {
    const indices = Array.from({ length: allWeeks.length }, (_, i) => i)
    if (sortOrder === "ascending") {
      indices.sort((a, b) => (Number(data[a]) || 0) - (Number(data[b]) || 0))
    } else if (sortOrder === "descending") {
      indices.sort((a, b) => (Number(data[b]) || 0) - (Number(data[a]) || 0))
    }
    return indices
  }, [allWeeks, data, sortOrder])

  const displayWeeks = sortedIndices.map(i => allWeeks[i])
  const displayData = sortedIndices.map(i => data[i] ?? null)
  const displayData2 = hasSecond ? sortedIndices.map(i => data2[i] ?? null) : []

  const actualDates = displayWeeks.map(w => dateByWeek[w] || null)
  const actualDates2 = displayWeeks.map(w => prevDateByWeek[w] || null)
  const xCategories = displayWeeks.map((_, i) => `${i + 1}`)

  // When only one week is in view (e.g. week filter applied), ApexCharts'
  // shared/intersect:false crosshair tracking fails to register a hover
  // region for a single category, so the tooltip never fires even though
  // the marker renders. Switch to direct-hover mode in that case.
  const singlePoint = displayWeeks.length <= 1

  const series = [
    { name: currentYear, data: displayData },
    ...(hasSecond ? [{ name: previousYear, data: displayData2 }] : []),
  ]

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 400,
        animateGradually: { enabled: true, delay: 100 },
        dynamicAnimation: { enabled: true, speed: 400 },
      },
      dropShadow: {
        enabled: true,
        color: gradientColors[0],
        top: 8,
        blur: 10,
        opacity: 0.25,
      },
    },

    dataLabels: { enabled: false },

    colors: hasSecond ? [gradientColors[0], "#FF3B7F"] : [gradientColors[0]],

    stroke: {
      curve: "smooth",
      width: hasSecond ? [3, 2.5] : 3,
      colors: hasSecond ? [gradientColors[0], "#FF3B7F"] : [gradientColors[0]],
      dashArray: hasSecond ? [0, 5] : [0],
    },

    markers: {
      size: singlePoint ? 6 : 4,
      colors: hasSecond ? [gradientColors[0], "#FF3B7F"] : [gradientColors[0]],
      strokeColors: ["#fff"],
      strokeWidth: 2,
      hover: { size: singlePoint ? 9 : 7 },
    },

    fill: {
      type: "gradient",
      gradient: {
        type: "vertical",
        shadeIntensity: 0,
        gradientToColors: hasSecond
          ? [gradientColors[1], "#FF3B7F"]
          : [gradientColors[1]],
        inverseColors: false,
        opacityFrom: hasSecond ? [0.35, 0.12] : 0.45,
        opacityTo: 0.02,
        stops: [0, 95, 100],
      },
    },

    xaxis: {
      categories: xCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 },
      },
      tooltip: {
        enabled: false,
      },
    },

    yaxis: {
      title: {
        text: title,
        style: { color: "#94a3b8", fontSize: "11px", fontWeight: 600 },
      },
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px" },
        formatter: val =>
          pound
            ? `£${val.toLocaleString("en-GB")}`
            : `${val.toLocaleString("en-GB")}%`,
      },
    },

    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },

    tooltip: {
      theme: "dark",
      shared: !singlePoint,
      intersect: singlePoint,
      style: { fontSize: "12px" },
      x: {
        formatter: (_val, { dataPointIndex }) => {
          const curLabel = getWeekTooltipLabel(
            dataPointIndex,
            actualDates[dataPointIndex]
          )
          const prevLabel = hasPreviousYearData
            ? getWeekTooltipLabel(
                dataPointIndex,
                actualDates2[dataPointIndex]
              )
            : "N/A"
          return `${currentYear} : ${curLabel}<br/>${previousYear} : ${prevLabel}`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined
            ? pound
              ? `£${val.toLocaleString("en-GB")}`
              : `${val.toLocaleString("en-GB")}%`
            : "N/A",
      },
      // Give preference to current year even when value is 0 (previous year = N/A/null)
      // Uses ApexCharts native markup/classes (apexcharts-tooltip-title / series-group)
      // so the tooltip stays visually uniform with the default dark theme used elsewhere.
      custom: ({ dataPointIndex }) => {
        const idx = dataPointIndex
        if (idx == null || idx < 0) return ""
        const curVal = displayData[idx]
        const prevVal = hasSecond ? displayData2[idx] : null
        const curLabel = getWeekTooltipLabel(idx, actualDates[idx])
        const prevLabel = hasPreviousYearData
          ? getWeekTooltipLabel(idx, actualDates2[idx])
          : "N/A"
        const formatVal = v =>
          v !== null && v !== undefined
            ? pound
              ? `£${Number(v).toLocaleString("en-GB")}`
              : `${Number(v).toLocaleString("en-GB")}%`
            : "N/A"
        const curFormatted = formatVal(curVal)
        const prevFormatted = formatVal(prevVal)
        const title = `${currentYear} : ${curLabel}<br/>${previousYear} : ${prevLabel}`
        // Return native ApexCharts tooltip inner HTML for uniform styling.
        // No inline font-size/font-family so it inherits tooltip.style.fontSize (12px)
        // and SCSS $font-family-base, matching PieChart/other tooltips.
        return `
          <div class="apexcharts-tooltip-title">${title}</div>
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${gradientColors[0]};"></span>
            <div class="apexcharts-tooltip-text">
              <div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">${currentYear}: </span><span class="apexcharts-tooltip-text-y-value">${curFormatted}</span></div>
            </div>
          </div>
          ${
            hasSecond
              ? `<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: #FF3B7F;"></span>
            <div class="apexcharts-tooltip-text">
              <div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">${previousYear}: </span><span class="apexcharts-tooltip-text-y-value">${prevFormatted}</span></div>
            </div>
          </div>`
              : ""
          }
        `
      },
    },

    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: 600,
      labels: { colors: ["#1e293b", "#64748b"] },
      markers: { radius: 3, width: 10, height: 3 },
    },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        padding: "20px 16px 8px",
        boxShadow:
          "0 4px 24px rgba(108,99,255,0.08), 0 1px 4px rgba(0,0,0,0.04)",
        border: "1px solid #ede9fe",
        position: "relative",
        overflow: "hidden",
        height: "100%",
      }}
      className="mb-4"
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`,
          borderRadius: "16px 16px 0 0",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          marginBottom: "4px",
          paddingLeft: "8px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
              boxShadow: `0 0 8px ${gradientColors[0]}88`,
            }}
          />
          <span
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#1e293b",
              letterSpacing: "-0.2px",
            }}
          >
            {title}
          </span>
        </div>

        {/* Sort Order cycling button */}
        <SortButton sortOrder={sortOrder} onToggle={handleToggle} />
      </div>

      <Chart type="area" options={options} series={series} height={320} />
    </div>
  )
}

export default AreaLineChart
